"use client";

import Image from "next/image";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { SearchableCombobox } from "@/components/ui/SearchableCombobox";
import logoOrange from "../../public/logo-orange.png";

// Past this many studios a plain <Select> is unusable, so the switcher becomes
// a searchable combobox instead.
const WORKSPACE_SEARCH_THRESHOLD = 10;

// The trigger is the band's wide logo with the pickers' chevron beside it.
const TRIGGER =
  "h-auto w-auto gap-2 rounded-lg border-0 bg-transparent px-2 py-1 hover:bg-stone-200/50 focus:ring-0 focus:ring-offset-0";

/**
 * The studio's logo: a 32px square when `compact`, otherwise 40px tall and
 * as wide as the image is, up to 15rem. A studio without an image shows the
 * Salesably mark, with the studio's name beside it at the wide size.
 */
export function WorkspaceLogo({ workspace, compact = false }) {
  const alt = workspace?.name ? `${workspace.name} Logo` : "Salesably Logo";
  if (compact) {
    return (
      <Image
        src={workspace?.image || logoOrange}
        alt={alt}
        width={32}
        height={32}
        className="h-8 w-8 rounded-md object-contain"
        priority
      />
    );
  }
  if (workspace?.image) {
    return (
      <Image
        src={workspace.image}
        alt={alt}
        width={240}
        height={40}
        className="h-10 w-auto max-w-60 rounded-md object-contain"
        priority
      />
    );
  }
  return (
    <span className="flex items-center gap-2">
      <Image
        src={logoOrange}
        alt={alt}
        width={40}
        height={40}
        className="h-10 w-10 object-contain"
        priority
      />
      <span className="truncate text-lg font-semibold text-gray-900">
        {workspace?.name || "Select Studio"}
      </span>
    </span>
  );
}

export default function WorkspaceSwitcher({
  currentWorkspace,
  workspaces,
  value,
  onSelect,
  isSwitching,
}) {
  if (!workspaces || workspaces.length === 0) return null;

  const studioSwitcherLabel = currentWorkspace?.name
    ? `Switch studio. Current studio: ${currentWorkspace.name}`
    : "Select a studio";
  const logo = <WorkspaceLogo workspace={currentWorkspace} />;

  if (workspaces.length > WORKSPACE_SEARCH_THRESHOLD) {
    return (
      <SearchableCombobox
        items={workspaces}
        selectedValue={value || undefined}
        onSelect={(id) => {
          if (id === currentWorkspace?.id) return;
          const workspace = workspaces.find((w) => w.id === id);
          if (workspace) onSelect(workspace);
        }}
        placeholder="Select a studio..."
        searchPlaceholder="Search studios..."
        emptyMessage="No studios found."
        ariaLabel={studioSwitcherLabel}
        disabled={isSwitching}
        className={TRIGGER}
        popoverContentClassName="w-[22rem] max-w-[calc(100vw-2rem)]"
        renderSelected={() => logo}
      />
    );
  }

  return (
    <Select
      value={value || undefined}
      disabled={isSwitching}
      onValueChange={(nextValue) => {
        if (nextValue === currentWorkspace?.id) return;
        const workspace = workspaces.find((w) => w.id === nextValue);
        if (workspace) {
          onSelect(workspace);
        }
      }}
    >
      <SelectTrigger
        aria-label={studioSwitcherLabel}
        disabled={isSwitching}
        className={TRIGGER}
      >
        {logo}
      </SelectTrigger>
      <SelectContent>
        {workspaces.map((workspace) => (
          <SelectItem
            key={workspace.id}
            value={workspace.id}
            className="text-base text-gray-700"
          >
            {workspace.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
